import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import styles from "./styles.module.scss";
import SkeletonLoader from "../../../components/skeleton-loader";
import Avatar from "../../../components/Avatar/Avatar";
import useTicketStore from "../../../stores/ticket";
import useCommonStore from "../../../stores/common";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import { AddCommentModal } from "../components/add-comment-modal";
import { fetchTicketDetails } from "../../../services/ticket";
import { getFileTypeInfo } from "../../../pages/create-app/components/application-form/components/file-upload/utils";
import { Copier } from "../../../../components/copier";
import CopyIcon from "../../../assets/svg/icons/common/copy";
import CloseIcon24 from "../../../assets/svg/icons/common/CloseIcon24";
import PlusIcon16 from "../../../assets/svg/icons/common/PlusIcon16";
import TicketCommentAttachment16 from "../../../assets/svg/icons/common/TicketCommentAttachment16";
import ShowFileModal from "../../../components/show-file-modal";
import TicketStatusIcon from "../components/TicketStatusIcon";
import {
  getTicketStatusIconType,
  getTicketStatusSlug,
} from "../ticketStatusUtils";

const Details = () => {
  const selectedTicket = useTicketStore((state) => state.selectedTicket);
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const [showAddComment, setShowAddComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docModal, setDocModal] = useState(false);
  const [docModalFile, setDocModalFile] = useState({
    path: "",
    name: "",
    type: ""
  });

  const openDocModal = (attachment) => {
    const type = attachment.file_path.split('.').pop();
    // file_path may be either a full S3 URL or a bare key starting with "service-ticket-attachments/".
    let path;
    try {
      const url = new URL(attachment.file_path);
      path = url.pathname.replace(/^\//, '');
    } catch (e) {
      path = attachment.file_path.replace(/^\//, '');
    }

    setDocModalFile({
      path: path,
      name: attachment.name,
      type: type
    });
    setDocModal(true);
  };

  useEffect(() => {
    if (showDetails && selectedTicket?.id) {
      setLoading(true);
      fetchTicketDetails(selectedTicket.id)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [showDetails, selectedTicket?.id]);

  if (!selectedTicket) return null;

  if (loading) {
    return (
      <div className={styles.details_container}>
        <div className={styles.header}>
          <button className={styles.close_button} onClick={() => setShowDetails(false)}>
            <CloseIcon24 />
          </button>
        </div>
        <div className={styles.content_wrapper}>
          <div className={styles.skeleton_content}>
            <SkeletonLoader variant="rectangular" height={28} width="60%" />
            <SkeletonLoader variant="rectangular" height={20} width={100} />
            <SkeletonLoader variant="rectangular" height={20} width={120} />
            <div className={styles.skeleton_grid}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <SkeletonLoader key={i} variant="rectangular" height={40} width="100%" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.details_container}>
      <div className={styles.header}>
        <button className={styles.close_button} onClick={() => setShowDetails(false)}>
          <CloseIcon24 />
        </button>
      </div>

      <div className={styles.content_wrapper}>
        <div className={styles.title_section}>
          <div className={styles.title}>
            <h1>{selectedTicket.problem_type}</h1>
            <div
              className={`${styles.status} ${styles[`status_${getTicketStatusSlug(selectedTicket.status)}`]}`}
            >
              <TicketStatusIcon
                type={getTicketStatusIconType(selectedTicket.status)}
                className={styles.statusIcon}
              />
              <span>{selectedTicket.status}</span>
            </div>
          </div>
          <ButtonIcon
            bgColor="#4C70EF"
            textColor="#fff"
            onClick={() => setShowAddComment(true)}
          >
            <PlusIcon16 />
            Add comment
          </ButtonIcon>
        </div>

        <div className={styles.ticket_info}>
          <div className={styles.info_grid}>
            <div className={styles.info_item}>
              <span className={styles.label}>Ticket #</span>
              <div className={styles.value_with_action}>
                <span>{selectedTicket.id}</span>
                <Copier text={selectedTicket.ticket}>
                  <button title="Copy ticket number">
                    <CopyIcon />
                  </button>
                </Copier>
              </div>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>DBA</span>
              <div className={styles.value_with_action}>
                <span>{selectedTicket.dba || '-'}</span>
                <Copier text={selectedTicket.dba || '-'}>
                  <button title="Copy DBA">
                    <CopyIcon />
                  </button>
                </Copier>
              </div>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>MID</span>
              <span className={styles.value}>{selectedTicket.mid || '-'}</span>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>External ID</span>
              <span className={styles.value}>{selectedTicket.external_id || '-'}</span>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>Processor</span>
              <div className={styles.value_with_action}>
                <div className={styles.processor_value}>
                  <span>{selectedTicket.processor?.toUpperCase() || '-'}</span>
                </div>
                <Copier text={selectedTicket.processor?.toUpperCase() || '-'}>
                  <button title="Copy processor">
                    <CopyIcon />
                  </button>
                </Copier>
              </div>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>Rep</span>
              <div className={styles.value_with_avatar}>
                <Avatar name={selectedTicket.rep?.charAt(0) ?? '-'} size="xs" />
                <span>{selectedTicket.rep || '-'}</span>
              </div>
            </div>

            <div className={styles.info_item}>
              <span className={styles.label}>Problem type</span>
              <span className={styles.value}>{selectedTicket.problem_type || '-'}</span>
            </div>
          </div>
        </div>

        <div className={styles.comments_section}>
          {Object.entries(selectedTicket.comments?.reduce((acc, comment) => {
            const date = new Date(comment.created_at);
            const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();

            if (!acc[monthYear]) {
              acc[monthYear] = [];
            }
            acc[monthYear].push(comment);
            return acc;
          }, {}) || {}).map(([monthYear, comments]) => (
            <React.Fragment key={monthYear}>
              <div className={styles.month_divider}>{monthYear}</div>
              <div className={styles.comments_list}>
                {comments.map((comment, index) => (
                  <div key={index} className={styles.comment}>
                    <div className={styles.comment_header}>
                      <Avatar name={comment.created_by?.charAt(0) ?? '-'} size="xs" />
                      <div className={styles.comment_meta}>
                        <span className={styles.author}>{comment.created_by ?? 'Unknown'}:</span>
                        <span className={styles.timestamp}>
                          {(() => {
                            const date = new Date(comment.created_at);
                            const formattedDate = date.toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            });
                            const formattedTime = date.toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: true
                            });
                            const timeZone = date.toLocaleTimeString('en-US', {
                              timeZoneName: 'short'
                            }).split(' ').pop();
                            return `${formattedDate} | ${formattedTime} (${timeZone})`;
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className={styles.comment_content}>
                      <div
                        className={styles.comment_text}
                        style={{ whiteSpace: 'pre-wrap' }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(comment.comment || ""),
                        }}
                      />
                      {comment.attachments?.length > 0 && (
                        <div className={styles.attachments}>
                          {comment.attachments.map((attachment, attachIndex) => {
                            const { color } = getFileTypeInfo(attachment.name);
                            return (
                              <div
                                key={attachIndex}
                                className={styles.attachment}
                                title={attachment.name}
                                onClick={() => openDocModal(attachment)}
                                style={{ cursor: 'pointer' }}
                              >
                                <TicketCommentAttachment16 color={color} />
                                <span>{attachment.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className={styles.comment_info}>
                        <div className={styles.info_row}>
                          <span className={styles.info_label}>Owner group</span>
                          <span className={styles.info_value}>{comment.owner_group?.name ?? '-'}</span>
                        </div>
                        <div className={styles.info_row}>
                          <span className={styles.info_label}>Problem type</span>
                          <span className={styles.info_value}>{comment.problem_type?.name ?? '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <AddCommentModal
        isOpen={showAddComment}
        onClose={() => setShowAddComment(false)}
        ticketId={selectedTicket?.id}
        canUpdateStatus={!!selectedTicket?.can_update_status}
      />

      <ShowFileModal
        isOpen={docModal}
        onClose={() => setDocModal(false)}
        path={docModalFile.path}
        name={docModalFile.name}
        type={docModalFile.type}
      />

    </div>
  );
};

export default Details;
